import { useEffect, useMemo } from 'react'

import { Flex, Header, Tabs, Text } from 'govwell-ui'
import { TabItem } from 'govwell-ui/components/Tabs/Tabs'
import { useLocalStorage } from 'usehooks-ts'

import { useLocation } from '@redwoodjs/router'

import AIPageContainer from 'src/components/AIAssistant/AIPageContainer'
import UnsavedChangesProtector from 'src/components/UnsavedChangesProtector/UnsavedChangesProtector'
import { useIsGovWellStaff, useMyOrg } from 'src/layouts/AppStateContextLayout/utils'
import AIAssistantInstallation from 'src/pages/AIAssistantSettingsPage/AIAssistantInstallation'
import CodeSnippet from 'src/pages/AIAssistantSettingsPage/CodeSnippet'
import CustomizationTab from 'src/pages/AIAssistantSettingsPage/CustomizationTab'
import EmailInsightsTab from 'src/pages/AIAssistantSettingsPage/EmailInsightsTab'
import { useLauncherSettingsForm } from 'src/pages/AIAssistantSettingsPage/launcherSettingsForm'
import TeamTab from 'src/pages/AIAssistantSettingsPage/TeamTab'
import { LocalStorageKey } from 'src/types'

export enum AIAssistantSettingsTabKey {
  Team = 'Team',
  EmailInsights = 'EmailInsights',
  Customization = 'Customization',
  Installation = 'Installation',
}

const AIAssistantSettingsPage = () => {
  const organization = useMyOrg()
  const { aiChatScriptUrl, isAIChatEnabled } = organization ?? {}
  const { search } = useLocation()
  const isGovWellStaff = useIsGovWellStaff()
  const showCustomizationTab = isGovWellStaff || !!organization?.aiChatIconCustomizationEnabled

  const [selectedTabKey, setSelectedTabKey] = useLocalStorage<AIAssistantSettingsTabKey>(
    LocalStorageKey.AIAssistantSettingsTabKey,
    AIAssistantSettingsTabKey.Team
  )

  const isValidTab = (value: string): value is AIAssistantSettingsTabKey =>
    Object.values(AIAssistantSettingsTabKey).includes(value as AIAssistantSettingsTabKey)

  // Support deep-linking to a specific tab via ?tab= query param
  useEffect(() => {
    const tabParam = new URLSearchParams(search).get('tab')
    if (tabParam && isValidTab(tabParam)) {
      setSelectedTabKey(tabParam)
    }
  }, [search, setSelectedTabKey])

  // handle the removal of the Usage tab
  useEffect(() => {
    if (!Object.values(AIAssistantSettingsTabKey).includes(selectedTabKey)) {
      setSelectedTabKey(AIAssistantSettingsTabKey.Team)
    }
  }, [selectedTabKey, setSelectedTabKey])
  const { form, onDiscardChanges, onSave } = useLauncherSettingsForm()

  const tabs: TabItem<AIAssistantSettingsTabKey>[] = useMemo(
    () => [
      {
        key: AIAssistantSettingsTabKey.Team,
        label: 'AI Admins',
        renderChildren: () => <TeamTab />,
      },
      {
        key: AIAssistantSettingsTabKey.EmailInsights,
        label: 'Email Insights',
        renderChildren: () => <EmailInsightsTab />,
      },
      ...(showCustomizationTab
        ? [
            {
              key: AIAssistantSettingsTabKey.Customization,
              label: 'Customization',
              renderChildren: () => <CustomizationTab />,
            },
          ]
        : []),
      {
        key: AIAssistantSettingsTabKey.Installation,
        label: 'Installation',
        renderChildren: () => (
          <Flex direction="column" gap={4}>
            <Flex direction="column" gap={2} align="start">
              <Text.Body>
                You can install the GovWell Community Assistant as an embedded widget in your
                organization&apos;s website. Configure the launcher here, then copy and paste the
                Install Script below.
              </Text.Body>
              <Text.Body>Contact the GovWell team via Intercom if you need help.</Text.Body>
            </Flex>
            <UnsavedChangesProtector
              hasUnsavedChanges={form.state.isDirty}
              onDiscardChanges={onDiscardChanges}
              onSave={onSave}
            >
              <AIAssistantInstallation isAIChatEnabled={!!isAIChatEnabled} form={form} />
            </UnsavedChangesProtector>
            <Flex direction="column" gap={2} align="start">
              <Header
                title="Install Script"
                subtitle="Paste this code in your web page. The Community Assistant launcher button will appear if the current URL matches one of the allowed URLs above. Use wildcard patterns (e.g., https://example.com/*) to match all pages on a domain."
              />
              <CodeSnippet
                code={`<script src="${aiChatScriptUrl}" type="text/javascript"></script>`}
              />
            </Flex>
          </Flex>
        ),
      },
    ],
    [aiChatScriptUrl, form, isAIChatEnabled, onDiscardChanges, onSave, showCustomizationTab]
  )

  return (
    <AIPageContainer
      title="Community Assistant Settings"
      subtitle="Configure Community Assistant access and installation"
    >
      <Tabs
        selectedTabKey={selectedTabKey}
        tabs={tabs}
        onSelectedTabKeyChange={setSelectedTabKey}
      />
      {tabs.find((i) => i.key === selectedTabKey)?.renderChildren?.()}
    </AIPageContainer>
  )
}

export default AIAssistantSettingsPage
